import React from "react";
import LegalShell, { LegalSection, LegalList } from "@/components/LegalShell";

// ============================================================================
// /privacy-policy — public legal page (no login required).
// ============================================================================
const CONTACT_EMAIL = "rigsentinel@gmail.com";

export default function PrivacyPolicy() {
  return (
    <LegalShell
      title="Privacy Policy"
      subtitle="This Privacy Policy explains how RigSentinel collects, uses, stores, and shares information when you use our Progressive Web Application. We are committed to data minimization: most of your operational data never leaves your device."
      lastUpdated="February 2026"
      testId="page-privacy-policy"
    >
      <LegalSection id="scope" title="1. Scope">
        <p>
          This Policy applies to information collected through the RigSentinel
          web application, our website, and our backend services used for
          authentication, billing, and AI-assisted text polishing.
        </p>
      </LegalSection>

      <LegalSection id="data-we-collect" title="2. Information We Collect">
        <p>
          <strong>a) Account data.</strong> Email address (used for
          passwordless magic-link sign-in) and authentication session tokens
          stored in secure HttpOnly cookies.
        </p>
        <p>
          <strong>b) Billing data.</strong> Subscription tier, billing status,
          and customer identifier returned by our payment processor (Stripe).
          We do not store full credit card numbers — payment details are
          handled directly by Stripe.
        </p>
        <p>
          <strong>c) Calculator and report data.</strong> Calculator inputs,
          saved calculations, favorites, well names, and report drafts are
          stored locally on your device using browser local storage. They are
          not transmitted to our servers.
        </p>
        <p>
          <strong>d) AI polishing / translation submissions.</strong> When you
          explicitly use the "Polish Report" or "Translate &amp; Polish"
          feature, the text you submit is sent to our backend and forwarded
          to the AI provider (OpenAI) solely to generate the requested
          output. We do not retain these submissions beyond what is required
          to fulfill the request.
        </p>
        <p>
          <strong>e) Technical data.</strong> Standard server logs (IP
          address, user agent, request timestamps) used for security,
          rate-limiting, and diagnostics. We do not run third-party analytics
          or advertising trackers.
        </p>
      </LegalSection>

      <LegalSection id="how-we-use" title="3. How We Use Information">
        <LegalList
          items={[
            "To authenticate you and maintain your session.",
            "To deliver the features you request, including subscription billing and AI polishing.",
            "To send transactional emails (magic-link sign-in, billing receipts).",
            "To detect, prevent, and respond to fraud, abuse, or security incidents.",
            "To comply with applicable law and respond to lawful requests.",
          ]}
        />
        <p>
          We do not sell, rent, or trade your personal information. We do not
          use your User Content to train AI models.
        </p>
      </LegalSection>

      <LegalSection id="processors" title="4. Service Providers and Sub-Processors">
        <p>
          We rely on a small set of trusted service providers to operate
          RigSentinel:
        </p>
        <LegalList
          items={[
            "Stripe — subscription billing and payment processing.",
            "Resend — transactional email delivery (magic-link sign-in).",
            "OpenAI — AI text generation for the Polish Report and Translate & Polish features (only when you explicitly trigger them).",
            "Cloud infrastructure providers used to host our backend and database.",
          ]}
        />
        <p>
          Each provider processes data on our behalf under their own privacy
          and security commitments.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="5. Data Retention">
        <p>
          Account and billing records are retained for as long as your account
          is active and for a reasonable period thereafter to satisfy legal,
          tax, accounting, or security obligations. Local-storage data
          (calculations, drafts, favorites) resides on your device only and
          can be cleared from your browser at any time.
        </p>
      </LegalSection>

      <LegalSection id="your-rights" title="6. Your Rights">
        <p>
          Depending on your jurisdiction, you may have the right to access,
          correct, port, restrict, or delete the personal information we hold
          about you, and to object to certain processing. To exercise these
          rights, contact us at{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="privacy-contact-email"
          >
            {CONTACT_EMAIL}
          </a>
          . We respond to verified requests within a reasonable timeframe.
        </p>
      </LegalSection>

      <LegalSection id="cookies" title="7. Cookies and Local Storage">
        <p>
          We use a single secure HttpOnly cookie to maintain your
          authenticated session. We use browser local storage to keep your
          calculator inputs, drafts, favorites, and unit preferences on your
          device. We do not use third-party advertising or tracking cookies.
        </p>
      </LegalSection>

      <LegalSection id="security" title="8. Security">
        <p>
          We use industry-standard practices to protect your data, including
          TLS in transit, restricted access to production systems, and
          short-lived authentication tokens. No internet-connected system is
          ever fully secure; you accept residual risk inherent to using any
          online service.
        </p>
      </LegalSection>

      <LegalSection id="children" title="9. Children">
        <p>
          RigSentinel is intended for professional oilfield use and is not
          directed at children under 18. We do not knowingly collect personal
          information from children.
        </p>
      </LegalSection>

      <LegalSection id="international" title="10. International Transfers">
        <p>
          Our service providers may process your information in countries
          other than your country of residence. Where required, we rely on
          appropriate safeguards for international transfers.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="11. Changes to This Policy">
        <p>
          We may update this Policy from time to time. Material changes will
          be communicated through the application or by email. The "Last
          updated" date above reflects the most recent revision.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="12. Contact">
        <p>
          Questions, requests, or privacy concerns can be sent to{" "}
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="underline"
            style={{ color: "var(--rs-amber)" }}
            data-testid="privacy-contact-email-bottom"
          >
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>
    </LegalShell>
  );
}
